import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { FaShoppingCart, FaUser, FaBars, FaTimes, FaHome, FaAtlas } from 'react-icons/fa';
import { HiAcademicCap } from 'react-icons/hi';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();



  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navLinks = [
    // { path: '/', label: 'Home', icon: <FaHome className="w-4 h-4" /> },
    { path: '/skills-analysis', label: 'Skills Analysis',  protected: true },
    { path: '/trending-skills', label: 'Trending Skills',  protected: true },
    { path: '/roadmap', label: 'Roadmap', protected: true },
    { path: '/community', label: 'Community', protected: true },
    { path: '/daily-quiz', label: 'Daily Quiz', protected: true },
    { path: '/resource-hub', label: 'Resources', protected: true },
    { path: '/mentors', label: 'Mentors', protected: true },
    { path: '/open-source',  label: 'Open Source', protected: true },
  ];

  // Modified: Mentor links moved to Profile page
  // if (user?.isMentor) {
  //     navLinks.push({ path: '/mentor/dashboard', label: 'Mentor Panel', protected: true });
  // } else {
  //     navLinks.push({ path: '/become-mentor', label: 'Join as Mentor', protected: true });
  // }

  const filteredNavLinks = navLinks.filter(link => !link.protected || user);

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0  left-0 right-0 z-50 transition-all duration-300 bg-white  ${
      isScrolled 
        ? 'shadow-medium border-b border-gray-100 py-2' 
        : 'shadow-soft py-4'
    }`}>
      <div className="container-responsive">
        <div className="flex items-center justify-between">
          {/* Logo Section - Takes 1/3 or flex-1 to push nav to center */}
          <div className="flex-1 flex items-center">
            <Link to="/" className="flex items-center space-x-3 group">
              {/* <div className={`transition-all duration-300 ${isScrolled ? 'w-9 h-9' : 'w-10 h-10 lg:w-12 lg:h-12'} bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center text-white font-bold shadow-soft group-hover:shadow-glow`}>
                  <img src="gaplify_nav_logo.png" alt="G" className='w-full h-full object-cover rounded-xl'/>
              </div> */}
              <div className="flex flex-col">
                <span className={`font-display font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent tracking-tight transition-all duration-300 ${isScrolled ? 'text-xl' : 'text-xl lg:text-2xl'}`}>Gaplify</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center">
             <div className="flex items-center  space-x-1 px-2 py-1.5 rounded-full transition-all duration-300">
                {filteredNavLinks.map((link) => (
                <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center px-4 py-2 rounded-full text-sm font-display font-medium transition-all duration-300 ${
                    isActive(link.path) 
                        ? 'bg-primary-50 text-primary-600 shadow-sm' 
                        : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                >
                    {link.label}
                </Link>
                ))}
             </div>
          </div>

          {/* Desktop Actions - Right Aligned */}
          <div className="hidden lg:flex flex-1 items-center justify-end space-x-3">
            {user ? (
              <>


                {/* Cart */}
                <Link
                  to="/cart"
                  className="relative flex items-center justify-center w-10 h-10 text-gray-600 hover:text-primary-600 hover:bg-white rounded-full transition-all duration-200 border border-transparent hover:border-gray-100 shadow-sm hover:shadow-md"
                >
                  <FaAtlas className="w-5 h-5" />
                  {cart.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold animate-bounce-in border-2 border-white">
                      {cart.length}
                    </span>
                  )}
                </Link>

                {/* Profile */}
                <Link 
                  to="/profile" 
                  className="flex items-center space-x-2 pl-1 pr-1 py-1 rounded-full hover:bg-white transition-all duration-200 border border-transparent hover:border-gray-100 hover:shadow-md group"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white shadow-soft group-hover:scale-105 transition-transform duration-200 ring-2 ring-white">
                   <FaUser className="w-4 h-4" />
                  </div>
                </Link>
              </>
            ) : (
              <>
                  <Link
                  to="/login"
                  className="px-5 py-2 text-sm font-display font-medium text-gray-600 hover:text-primary-600 transition-colors"
                  >
                  Login
                  </Link>
                  <Link
                  to="/signup"
                  className="px-5 py-2.5 text-sm font-display font-medium bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg shadow-primary-500/30 hover:shadow-primary-500/40 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                  Get Started
                  </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex-1 flex justify-end">
            <button
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
            >
            {isMenuOpen ? (
                <FaTimes className="w-6 h-6" />
            ) : (
                <FaBars className="w-6 h-6" />
            )}
            </button>
          </div>
        </div>


        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-fade-in-down">
            <div className="flex flex-col space-y-2">
              {filteredNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center px-4 py-3 rounded-lg text-sm font-display font-medium transition-all duration-200 ${
                    isActive(link.path) 
                      ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-600' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span className="mr-3 text-lg">{link.icon}</span>
                  {link.label}
                </Link>
              ))}
              
              {user && (
                <>
                  <div className="border-t border-gray-100 pt-4 mt-4">
                   
                    


                    <Link
                      to="/cart"
                      className="flex items-center justify-between px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 font-display font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="flex items-center">
                        <FaAtlas className="w-5 h-5 mr-3" />
                        <span>Library</span>
                      </div>
                      {cart.length > 0 && (
                        <span className="bg-accent-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                          {cart.length}
                        </span>
                      )}
                    </Link>
                    

                    
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg transition-all duration-200 font-display font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FaUser className="w-5 h-5 mr-3" />
                      <span>Profile</span>
                    </Link>
                    
                  
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;